<div class="sf-group-panel"
     [class]="'sf-depth-' + depth"
     [class.sf-collapsed]="collapsed()"
     [class.sf-mode-column]="columnMode"
     [attr.data-path]="rowPath()"
     [ngStyle]="{ '--sf-depth': depth }">

    @if (!columnMode) {
        <!-- Group title — always rendered so the panel is always collapsible.
             Title row carries the path badge + a chevron toggle. -->
        <div class="sf-group-title" (click)="toggleCollapsed($event)">
            <button type="button" mat-icon-button class="sf-collapse-btn"
                    (click)="toggleCollapsed($event)"
                    [matTooltip]="collapsed() ? 'Expand' : 'Collapse'"
                    [attr.aria-label]="collapsed() ? 'Expand section' : 'Collapse section'">
                <mat-icon class="sf-chevron" [class.sf-chevron-collapsed]="collapsed()">
                    expand_more
                </mat-icon>
            </button>
            <code class="sf-group-path">{{ rowPath() }}</code>
        </div>
    }

    @if (!collapsed() || columnMode) {
        <form [formGroup]="form" class="sf-form">
            @for (key of fieldKeys; track key) {
                @let field = schema.properties[key];

                @if (widgetFor(field) === 'object') {
                    @if (columnMode) {
                        <div class="sf-nav-row sf-nav-container"
                             [class.sf-nav-selected]="selectedSegment === key"
                             [attr.data-path]="leafPath(key)"
                             (click)="openPath.emit(key)">
                            <mat-icon class="sf-nav-kind-icon">folder</mat-icon>
                            <span class="sf-nav-name">{{ fieldLabel(key, field) }}</span>
                            <span class="sf-nav-preview">{{ containerPreviewBrief(key, field) }}</span>
                            <mat-icon class="sf-nav-chevron">chevron_right</mat-icon>
                        </div>
                    } @else {
                        <app-schema-form class="sf-nested"
                                [schema]="field"
                                [value]="form.get(key)?.value"
                                [disabled]="disabled"
                                [pathPrefix]="childPath(key)"
                                (valueChange)="form.get(key)?.patchValue($event, { emitEvent: true })"
                                (focusPath)="focusPath.emit($event)"
                                (pathCleared)="pathCleared.emit($event)" />
                    }
                } @else if (widgetFor(field) === 'array-objects') {
                    @if (columnMode) {
                        <div class="sf-nav-row sf-nav-container"
                             [class.sf-nav-selected]="selectedSegment === key"
                             [attr.data-path]="leafPath(key)"
                             (click)="openPath.emit(key)">
                            <mat-icon class="sf-nav-kind-icon">data_array</mat-icon>
                            <span class="sf-nav-name">{{ fieldLabel(key, field) }}</span>
                            <span class="sf-nav-preview">{{ containerPreviewBrief(key, field) }}</span>
                            <mat-icon class="sf-nav-chevron">chevron_right</mat-icon>
                        </div>
                    } @else {
                        <!-- Array-of-objects — peer-depth panel (depth+1).
                             Items inside recurse at depth+2 via pathPrefix
                             "[N]" markers. -->
                        <div class="sf-group-panel sf-array-panel"
                             [class]="'sf-depth-' + (depth + 1)"
                             [class.sf-collapsed]="isArrayCollapsed(key)"
                             [attr.data-path]="leafPath(key)"
                             [ngStyle]="{ '--sf-depth': (depth + 1) }">
                            <div class="sf-group-title" (click)="toggleArrayCollapsed(key, $event)">
                                <button type="button" mat-icon-button class="sf-collapse-btn"
                                        (click)="toggleArrayCollapsed(key, $event)"
                                        [matTooltip]="isArrayCollapsed(key) ? 'Expand' : 'Collapse'"
                                        [attr.aria-label]="isArrayCollapsed(key) ? 'Expand list' : 'Collapse list'">
                                    <mat-icon class="sf-chevron" [class.sf-chevron-collapsed]="isArrayCollapsed(key)">
                                        expand_more
                                    </mat-icon>
                                </button>
                                <code class="sf-group-path">{{ leafPath(key) }}</code>
                                <span class="sf-array-name">{{ fieldLabel(key, field) }} · {{ arrayObjectsValue(key).length }}</span>
                                <span class="sf-flex-spacer"></span>
                                @if (!isReadOnly(field)) {
                                    <button type="button" mat-stroked-button class="sf-array-add"
                                            (click)="addArrayObjectItem(key, field, $event)">
                                        <mat-icon>add</mat-icon> Add
                                    </button>
                                }
                            </div>
                            @if (!isArrayCollapsed(key)) {
                                @for (item of arrayObjectsValue(key); track $index; let i = $index) {
                                    <div class="sf-array-item-wrapper">
                                        <app-schema-form class="sf-nested"
                                                [schema]="field.items"
                                                [value]="item"
                                                [disabled]="disabled || isReadOnly(field)"
                                                [pathPrefix]="arrayItemPath(key, i)"
                                                (valueChange)="onArrayObjectItemChange(key, i, $event)"
                                                (focusPath)="focusPath.emit($event)"
                                                (pathCleared)="pathCleared.emit($event)" />
                                        @if (!isReadOnly(field)) {
                                            <button type="button" mat-icon-button class="sf-array-remove"
                                                    (click)="removeArrayObjectItem(key, i, $event)"
                                                    matTooltip="Remove this item"
                                                    aria-label="Remove this item">
                                                <mat-icon>remove_circle_outline</mat-icon>
                                            </button>
                                        }
                                    </div>
                                }
                                @if (arrayObjectsValue(key).length === 0) {
                                    <div class="sf-array-empty">No items — click <em>Add</em> to create the first one.</div>
                                }
                            }
                        </div>
                    }
                } @else {
                    @if (columnMode) {
                        <!-- Leaf nav row — click to open the leaf editor in
                             the next column. Shows the current value as a
                             preview so the operator sees state at a glance. -->
                        <div class="sf-nav-row sf-nav-leaf"
                             [class.sf-nav-selected]="selectedSegment === key"
                             [class.sf-nav-readonly]="isReadOnly(field)"
                             [attr.data-path]="leafPath(key)"
                             (click)="openPath.emit(key)">
                            <mat-icon class="sf-nav-kind-icon">{{ leafIcon(field) }}</mat-icon>
                            <span class="sf-nav-name">
                                {{ fieldLabel(key, field) }}
                                @if (isReadOnly(field)) {
                                    <mat-icon class="sf-lock-icon" matTooltip="Read-only">lock</mat-icon>
                                }
                            </span>
                            <span class="sf-nav-preview">{{ leafPreview(key, field) }}</span>
                            @if (isReadOnly(field)) {
                                <mat-icon class="sf-nav-readonly-icon" matTooltip="Read-only">lock</mat-icon>
                            } @else {
                                <mat-icon class="sf-nav-chevron">chevron_right</mat-icon>
                            }
                        </div>
                    } @else {
                    <div class="sf-row"
                         [class.sf-row-readonly]="isReadOnly(field)"
                         [attr.data-path]="leafPath(key)"
                         (focusin)="focusPath.emit(leafPath(key))">
                        <span class="sf-name" [title]="fieldHint(field) || ''">
                            {{ fieldLabel(key, field) }}
                            @if (isReadOnly(field)) {
                                <mat-icon class="sf-lock-icon" matTooltip="Read-only — defined on the UserDataType">lock</mat-icon>
                            }
                        </span>
                        <div class="sf-editor">
                            <ng-container [ngSwitch]="widgetFor(field)">

                                <!-- ── string (free-text) ── -->
                                <mat-form-field *ngSwitchCase="'text'" appearance="outline" class="sf-field" subscriptSizing="dynamic">
                                    <input matInput [formControlName]="key"
                                           [placeholder]="fieldPlaceholder(field)" />
                                    <mat-error *ngIf="form.get(key)?.hasError('required')">Required</mat-error>
                                    <mat-error *ngIf="form.get(key)?.hasError('minlength')">Min {{ field.minLength }} chars</mat-error>
                                    <mat-error *ngIf="form.get(key)?.hasError('maxlength')">Max {{ field.maxLength }} chars</mat-error>
                                    <mat-error *ngIf="form.get(key)?.hasError('pattern')">Pattern mismatch</mat-error>
                                </mat-form-field>

                                <!-- ── string (enum) ── -->
                                <mat-form-field *ngSwitchCase="'enum'" appearance="outline" class="sf-field" subscriptSizing="dynamic">
                                    <mat-select [formControlName]="key">
                                        @for (opt of field.enum; track opt) {
                                            <mat-option [value]="opt">{{ opt }}</mat-option>
                                        }
                                    </mat-select>
                                    <mat-error *ngIf="form.get(key)?.hasError('required')">Required</mat-error>
                                </mat-form-field>

                                <!-- ── string + x-options.source = "userdata-*" autocomplete ── -->
                                <mat-form-field *ngSwitchCase="'autocomplete'" appearance="outline" class="sf-field" subscriptSizing="dynamic">
                                    <input matInput [formControlName]="key"
                                           [placeholder]="fieldPlaceholder(field)"
                                           [matAutocomplete]="acAuto" />
                                    <mat-autocomplete #acAuto="matAutocomplete">
                                        @for (opt of filteredFor(key, field['x-options'].source); track opt) {
                                            <mat-option [value]="opt">{{ opt }}</mat-option>
                                        }
                                    </mat-autocomplete>
                                    <mat-error *ngIf="form.get(key)?.hasError('required')">Required</mat-error>
                                </mat-form-field>

                                <!-- ── secret (masked input) — capability isSecret ── -->
                                <mat-form-field *ngSwitchCase="'secret'" appearance="outline" class="sf-field" subscriptSizing="dynamic">
                                    <input matInput type="password" autocomplete="new-password"
                                           [formControlName]="key"
                                           [placeholder]="fieldPlaceholder(field)" />
                                    <mat-icon matSuffix
                                              matTooltip="Secret — value is masked and stored securely."
                                              class="sf-secret-icon">lock</mat-icon>
                                    <mat-error *ngIf="form.get(key)?.hasError('required')">Required</mat-error>
                                    <mat-error *ngIf="form.get(key)?.hasError('minlength')">Min {{ field.minLength }} chars</mat-error>
                                    <mat-error *ngIf="form.get(key)?.hasError('maxlength')">Max {{ field.maxLength }} chars</mat-error>
                                    <mat-error *ngIf="form.get(key)?.hasError('pattern')">Pattern mismatch</mat-error>
                                </mat-form-field>

                                <!-- ── string + x-options.source = "userdata" → catalog picker ── -->
                                <div *ngSwitchCase="'userdata-picker'" class="sf-userdata-cell"
                                     [class.sf-userdata-leased]="isResource(field)">
                                    <app-user-data-picker
                                            [type]="userDataPickerType(field)"
                                            [label]="''"
                                            [required]="!!schema.required?.includes(key)"
                                            [value]="form.get(key)?.value"
                                            (valueChange)="form.get(key)?.setValue($event)"
                                            [placeholder]="fieldPlaceholder(field)" />
                                    @if (isResource(field)) {
                                        <span class="sf-leased-chip"
                                              matTooltip="This entity will be exclusively leased via the Resource Manager for the duration of the execution.">
                                            <mat-icon class="sf-leased-icon">vpn_lock</mat-icon>
                                            Leased
                                        </span>
                                    }
                                </div>

                                <!-- ── boolean list (cardinality=list) — checkbox group ── -->
                                <div *ngSwitchCase="'boolean-list'" class="sf-checkbox-group">
                                    @for (opt of booleanListOptions(field); track opt.value) {
                                        <mat-checkbox color="primary"
                                                      [checked]="isBooleanListChecked(key, opt.value)"
                                                      [disabled]="isReadOnly(field)"
                                                      (change)="onBooleanListToggle(key, opt.value, $event)">
                                            {{ opt.label }}
                                        </mat-checkbox>
                                    }
                                </div>

                                <!-- ── Connector field → live connection picker (nucleus-connectors-service) ── -->
                                <div *ngSwitchCase="'connector-catalog'" class="sf-userdata-cell"
                                     [class.sf-userdata-leased]="isResource(field)">
                                    <app-connection-picker
                                            [type]="connectorTypeParts(field).type"
                                            [subtype]="connectorTypeParts(field).subtype"
                                            [label]="''"
                                            [required]="!!schema.required?.includes(key)"
                                            [value]="form.get(key)?.value"
                                            (valueChange)="form.get(key)?.setValue($event)"
                                            [placeholder]="fieldPlaceholder(field)" />
                                    @if (isResource(field)) {
                                        <span class="sf-leased-chip"
                                              matTooltip="This connection will be exclusively leased via the Resource Manager for the duration of the execution.">
                                            <mat-icon class="sf-leased-icon">vpn_lock</mat-icon>
                                            Leased
                                        </span>
                                    }
                                </div>

                                <!-- ── integer / number ── -->
                                <mat-form-field *ngSwitchCase="'number'" appearance="outline" class="sf-field" subscriptSizing="dynamic">
                                    <input matInput type="number" [formControlName]="key"
                                           [min]="field.minimum ?? null" [max]="field.maximum ?? null"
                                           [placeholder]="fieldPlaceholder(field)" />
                                    <mat-error *ngIf="form.get(key)?.hasError('required')">Required</mat-error>
                                    <mat-error *ngIf="form.get(key)?.hasError('min')">Min {{ field.minimum }}</mat-error>
                                    <mat-error *ngIf="form.get(key)?.hasError('max')">Max {{ field.maximum }}</mat-error>
                                </mat-form-field>

                                <!-- ── boolean ── -->
                                <div *ngSwitchCase="'boolean'" class="sf-toggle-cell">
                                    <mat-slide-toggle [formControlName]="key" color="primary" />
                                </div>

                                <!-- ── array of primitives — chip input ── -->
                                <mat-form-field *ngSwitchCase="'array'" appearance="outline" class="sf-field" subscriptSizing="dynamic">
                                    <mat-chip-grid #chipGrid [formControlName]="key">
                                        @for (item of arrayValue(key); track item) {
                                            <mat-chip-row (removed)="arrayRemove(key, item)">
                                                {{ item }}
                                                <button matChipRemove [attr.aria-label]="'Remove ' + item">
                                                    <mat-icon>cancel</mat-icon>
                                                </button>
                                            </mat-chip-row>
                                        }
                                    </mat-chip-grid>
                                    <input [placeholder]="fieldPlaceholder(field) || 'Add and press Enter…'"
                                           [matChipInputFor]="chipGrid"
                                           [matChipInputSeparatorKeyCodes]="chipSeparators"
                                           (matChipInputTokenEnd)="arrayAdd(key, $event)" />
                                </mat-form-field>

                            </ng-container>
                        </div>
                        @if (hasValue(key) && !isReadOnly(field)) {
                            <button type="button" mat-icon-button class="sf-clear"
                                    [disabled]="disabled"
                                    (click)="clearField(key, field, $event)"
                                    matTooltip="Clear value"
                                    aria-label="Clear value">
                                <mat-icon>delete_outline</mat-icon>
                            </button>
                        } @else {
                            <span class="sf-clear-placeholder"></span>
                        }
                    </div>
                    }
                }
            }
        </form>
    }
</div>
